import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import {
    useFloating,
    useClick,
    useDismiss,
    useRole,
    useInteractions,
    FloatingFocusManager,
    offset,
    flip,
    size,
    autoUpdate,
    useMergeRefs,
    useListNavigation,
} from '@floating-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import PropTypes from 'prop-types';
import Spinner from 'Components/Atoms/Spinner';
import OutlineChevronDown from 'Components/Atoms/OutlineIcons/OutlineChevronDown';
import selectVariants from 'Components/Atoms/Select/SelectVariants';
import { cn } from 'Utils/cn';
import MultiselectFilter from 'Components/Molecules/Multiselect/MultiselectFilter';
import ButtonClear from 'Components/Atoms/ButtonClear/ButtonClear';
import IconButton from 'Components/Atoms/IconButton/IconButton';
import OutlineArrowBottom from 'Components/Atoms/OutlineIcons/OutlineArrowBottom';
import ErrorMessage from 'Components/Atoms/ErrorMessage/ErrorMessage';
import { useTranslation } from 'react-i18next';
import FloatLabel from 'Components/Atoms/FloatLabel/FloatLabel';

const SelectInfinite = forwardRef(function SelectInfinite(props, ref) {
    const {
        label,
        floatLabel,
        size: selectSize,
        options,
        value,
        onChange,
        className,
        disabled,
        query,
        onChangeQuery,
        isFetching,
        fetchNextPage,
        hasNextPage,
        enableClear = false,
        onClear,
        actions = [],
        error,
        emptyMessage,
        placeholder,
        ...rest
    } = props;
    const sentinelRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(null);
    const listRef = useRef([]);

    const handleFilter = (ev) => {
        onChangeQuery(ev.target.value);
        setActiveIndex(null);
    };

    const { t } = useTranslation('common');
    const { refs, floatingStyles, context, strategy, x, y } = useFloating({
        open: isOpen,
        onOpenChange: (open) => {
            setIsOpen(open);
            if (!open) {
                onChangeQuery('');
                setActiveIndex(null);
            }
        },
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(4),
            flip(),
            size({
                apply({ rects, elements, availableHeight }) {
                    Object.assign(elements.floating.style, {
                        minWidth: `${rects.reference.width}px`,
                        maxHeight: `${Math.min(availableHeight - 20, 520)}px`,
                    });
                },
            }),
        ],
    });

    useEffect(() => {
        if (sentinelRef.current) {
            const observer = new window.IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && hasNextPage && !isFetching) {
                        fetchNextPage();
                    }
                });
            });
            observer.observe(sentinelRef.current);
            return () => observer.disconnect();
        }
    }, [sentinelRef, hasNextPage, isFetching, isOpen]);

    const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
        useClick(context),
        useRole(context, { role: 'listbox' }),
        useDismiss(context),
        useListNavigation(context, {
            listRef,
            activeIndex,
            onNavigate: setActiveIndex,
            virtual: true,
            loop: true,
            focusItemOnOpen: true,
        }),
    ]);

    const handleFloatingKeyDown = useCallback(
        (e) => {
            if ((e.key === 'Enter' || e.key === ' ') && activeIndex != null && options[activeIndex]) {
                e.preventDefault();
                onChange(options[activeIndex].id);
                setIsOpen(false);
                onChangeQuery('');
            }
        },
        [activeIndex, options],
    );

    const buttonRef = useMergeRefs([refs.setReference, ref]);
    const hasError = !!error;

    return (
        <div className="relative min-w-0 group">
            <FloatLabel label={floatLabel} hasError={hasError} isOpen={isOpen} />
            <button
                ref={buttonRef}
                type="button"
                disabled={disabled}
                className={cn(
                    selectVariants({ size: selectSize, status: hasError ? 'error' : 'defualt', className }),
                    'gap-1',
                    isOpen && 'border-primary ring-2 ring-primary/20',
                    !label && 'text-dark-blue/50',
                )}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                {...getReferenceProps(rest)}
            >
                <div className="truncate grow min-w-0">{label || placeholder}</div>
                {enableClear && !disabled && <ButtonClear onClear={onClear} />}
                <OutlineChevronDown size={20} className="shrink-0 text-dark-blue" color="currentColor" />
            </button>
            <AnimatePresence>
                {isOpen && (
                    <FloatingFocusManager context={context}>
                        <motion.div
                            ref={refs.setFloating}
                            style={{ ...floatingStyles, position: strategy, top: y ?? 0, left: x ?? 0 }}
                            className="mt-1 w-full overflow-x-hidden overflow-y-auto scrollbar-thin rounded-md bg-white shadow-sm ring-1 ring-black/5 focus:outline-none text-sm z-dropdown min-w-[180px]"
                            tabIndex={-1}
                            {...getFloatingProps({ onKeyDown: handleFloatingKeyDown })}
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.15, ease: 'easeOut' }}
                        >
                            <MultiselectFilter value={query} onChange={handleFilter} isBusy={isFetching} />

                            <div className="p-1.5 min-h-14">
                                {!isFetching && options.length === 0 && !query && (
                                    <div className="text-center text-gray-500 text-sm py-6">{emptyMessage}</div>
                                )}
                                {!isFetching && options.length === 0 && !!query && (
                                    <div className="text-center text-gray-500 text-sm py-6">
                                        {t('no-results-found')}
                                    </div>
                                )}
                                {options.map((option, idx) => (
                                    <div
                                        key={option.id}
                                        ref={(node) => {
                                            listRef.current[idx] = node;
                                        }}
                                        tabIndex={activeIndex === idx ? 0 : -1}
                                        className={cn(
                                            'cursor-pointer select-none px-3 py-2 rounded-sm transition-colors outline-none',
                                            value === option.id
                                                ? 'bg-primary/10 text-primary font-semibold'
                                                : activeIndex === idx
                                                  ? 'bg-gray-100'
                                                  : 'hover:bg-gray-100',
                                            option.className,
                                        )}
                                        role="option"
                                        aria-selected={value === option.id}
                                        {...getItemProps({
                                            onClick() {
                                                onChange(option.id);
                                                setIsOpen(false);
                                                onChangeQuery('');
                                            },
                                        })}
                                    >
                                        {option.label}
                                    </div>
                                ))}
                                {hasNextPage && (
                                    <div
                                        className="text-center text-gray-500 text-xs flex justify-center py-2"
                                        ref={sentinelRef}
                                    >
                                        {isFetching ? (
                                            <Spinner />
                                        ) : (
                                            <IconButton
                                                variant="ghost"
                                                icon={<OutlineArrowBottom />}
                                                onClick={fetchNextPage}
                                            />
                                        )}
                                    </div>
                                )}
                            </div>

                            {actions.length > 0 && (
                                <div
                                    className="border-t bg-white border-gray-200 p-1.5 sticky bottom-0 gap-2"
                                    onClick={() => setIsOpen(false)}
                                >
                                    <div className="flex flex-col gap-1">{actions}</div>
                                </div>
                            )}
                        </motion.div>
                    </FloatingFocusManager>
                )}
            </AnimatePresence>

            <ErrorMessage message={error} />
        </div>
    );
});

SelectInfinite.propTypes = {
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    floatLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    options: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
        }),
    ).isRequired,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    className: PropTypes.string,
    query: PropTypes.string.isRequired,
    onChangeQuery: PropTypes.func.isRequired,
    isFetching: PropTypes.bool,
    fetchNextPage: PropTypes.func,
    hasNextPage: PropTypes.bool,
    enableClear: PropTypes.bool,
    onClear: PropTypes.func,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    size: PropTypes.oneOf(['normal', 'small']),
    actions: PropTypes.arrayOf(PropTypes.node),
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    emptyMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    placeholder: PropTypes.string,
};

export default SelectInfinite;
